import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ProfileTab from './tabs/ProfileTab';
import AccountingTab from './tabs/AccountingTab';
import UsersTab from './tabs/UsersTab';
import { settingsService } from '../../services/settingsService';
import { useClient } from '../../context/ClientContext';

const { FiSave, FiCheckCircle, FiAlertCircle, FiSettings, FiBriefcase, FiUsers, FiDollarSign } = FiIcons;

export default function CompanySetup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshProfile } = useClient();
  
  const queryParams = new URLSearchParams(location.search);
  const isCreateMode = queryParams.get('action') === 'create';

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [companyData, setCompanyData] = useState({
    name: '',
    legal_name: '',
    email: '',
    website: '',
    tax_id: '',
    fiscal_year_end: 'December',
    currency: 'USD',
    accounting_method: 'cash',
    industry: '',
    address: '',
    phone: '',
    entity_type: ''
  });

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getCompanyDetails();
      if (data) {
        setCompanyData({
          name: data.name || '',
          legal_name: data.legal_name || '',
          email: data.email || '',
          website: data.website || '',
          tax_id: data.tax_id || '',
          fiscal_year_end: data.fiscal_year_end || 'December',
          currency: data.currency || 'USD',
          accounting_method: data.accounting_method || 'cash',
          industry: data.industry || '',
          address: data.address || '',
          phone: data.phone || '',
          entity_type: data.entity_type || ''
        });
      }
    } catch (err) {
      setError("Failed to load company settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isCreateMode) {
      fetchDetails();
    } else {
      setLoading(false);
    }
  }, [isCreateMode]);

  const handleSave = async () => {
    try {
      if (!companyData.name && !companyData.legal_name) {
        setError("Business name is required.");
        return;
      }

      setSaving(true);
      setError(null);
      setSuccess(false);

      if (isCreateMode) {
        await settingsService.createBusiness(companyData);
        if (refreshProfile) await refreshProfile();
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        await settingsService.updateCompanyDetails(companyData);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      setError(err.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const tabs = isCreateMode 
    ? [
        { id: 'profile', label: 'Business Profile', icon: FiBriefcase }
      ]
    : [
        { id: 'profile', label: 'Business Profile', icon: FiBriefcase },
        { id: 'accounting', label: 'Accounting', icon: FiDollarSign },
        { id: 'users', label: 'Team', icon: FiUsers },
      ];

  const title = isCreateMode ? 'Create Business' : 'Company Settings';
  const subtitle = isCreateMode 
    ? 'Set up your new business profile and details.' 
    : 'Manage your business identity, financial rules, and team access.';
  const buttonText = isCreateMode 
    ? (saving ? 'Creating...' : 'Create Business') 
    : (saving ? 'Saving...' : 'Save Changes');

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Loading Settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto py-8 px-4 sm:px-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
              <SafeIcon icon={FiSettings} />
            </div>
            {title}
          </h1>
          <p className="text-slate-500 text-sm mt-1 ml-13">{subtitle}</p>
        </div>

        <div className="flex items-center gap-3">
          {success && (
            <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs animate-in fade-in slide-in-from-right-4">
              <SafeIcon icon={FiCheckCircle} />
              {isCreateMode ? 'Business Created' : 'Settings Saved'}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-rose-600 font-bold text-xs">
              <SafeIcon icon={FiAlertCircle} />
              {error}
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg shadow-slate-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <SafeIcon icon={FiSave} className={saving ? 'animate-pulse' : 'group-hover:scale-110 transition-transform'} />
            {buttonText}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Navigation Sidebar */}
        <div className="w-full md:w-64 shrink-0">
          <div className="bg-white border border-slate-200 rounded-[32px] p-2 shadow-sm sticky top-24">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-4 rounded-2xl font-bold text-sm transition-all mb-1 last:mb-0 ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <SafeIcon icon={tab.icon} className={activeTab === tab.id ? 'text-blue-400' : ''} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-white border border-slate-200 rounded-[40px] p-8 md:p-10 shadow-sm min-h-[600px]">
          {activeTab === 'profile' && (
            <ProfileTab 
              data={companyData} 
              onChange={(newData) => setCompanyData({ ...companyData, ...newData })} 
            />
          )}
          {activeTab === 'accounting' && (
            <AccountingTab 
              data={companyData} 
              onChange={(newData) => setCompanyData({ ...companyData, ...newData })} 
            />
          )}
          {activeTab === 'users' && <UsersTab />}
        </div>
      </div>
    </div>
  );
}